import { RefObject, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames/bind';
import styles from './List.module.css';

import { useUploadLibFileMutation } from '../../services/library';
import { useAppDispatch, useAppSelector } from '../../hooks/useRedux';
import useList from '../../hooks/useList';
import { useSlice } from '../../hooks/useSlice';
import useModal from '../../hooks/useModal';
import { useIntersect } from '../../hooks/useIntersect';
import { usePage } from '../../hooks/usePage';
import useHover from '../../hooks/useHover';

import { selectMenu } from '../../features/infoSlice';
import { selectSearchWord } from '../../features/searchSlice';

import SimpleBar from 'simplebar-react';
import useSimpleBar from '../../hooks/useSimpleBar';

import Header from './Header';
import Book from '../Book';
import Input from '../Input';
import UploadIndicator from '../UploadIndicator';
import emptySearch from '../../assets/images/search_result_empty.svg';
import emptyTrash from '../../assets/images/trash_empty.svg';
import { Options } from '../../types/library';
import { isMobile } from '../../utils/util';

import library_empty from '../../assets/images/result_empty.svg';
import {
  EMPTY_EBOOK,
  EMPTY_TRASH_MESSAGE,
  NO_PUBLIC_EBOOKS,
} from '../../constants';

export default function List() {
  const cx = classNames.bind(styles);
  const dispatch = useAppDispatch();
  const menu = useAppSelector(selectMenu);
  const searchWord = useAppSelector(selectSearchWord);
  const listRef = useRef<HTMLUListElement>(null);

  const slice = useSlice();
  const { selectOptions, selectSelectedIds } = slice;
  const options: Options = useAppSelector(selectOptions);
  const selectedIds = useAppSelector(selectSelectedIds);
  const { layoutType, isDetailsOpened, sortType, sortOrder } = options;
  const { page, pageChanged, initPageList, totalLen } = usePage();
  const { toggleModal } = useModal();
  const [isHover, hoverEventHandlers] = useHover();

  const [simplebarRef] = useSimpleBar();
  const { list, isSuccess: isListSuccess } = useList();
  const [isLastPage, setIsLastPage] = useState(false);
  const bookItemClass = menu !== 'main' ? 'library' : '';

  const [isLoading, setIsLoading] = useState(false);

  const [uploadFile, { status }] = useUploadLibFileMutation({
    fixedCacheKey: 'shared-update-list',
  });

  const location = useLocation();
  const folderId = location?.state?.folderId;

  const intersectRef = useIntersect(
    async (entry, observer) => {
      observer.unobserve(entry.target);

      if (isLoading || totalLen % 12 || isLastPage) return;

      setIsLoading(true);

      await dispatch(
        initPageList({
          ...(folderId && { folderId }),
          page: page,
          sortBy: sortType,
          sortDir: sortOrder,
          ...((menu === 'search' && { search: searchWord }) || {}),
        })
      )
        .unwrap()
        .then((res: { ids: []; entities: [] }) => {
          dispatch(pageChanged(page + 1));
          setIsLoading(false);
          page && list.length === res.ids.length && setIsLastPage(true);
        });
    },
    undefined,
    listRef
  );

  if (!isListSuccess) return null;

  function Empty() {
    let empty = null;
    if (menu === 'search') {
      empty = (
        <div className={cx('result_empty')}>
          <img
            src={emptySearch}
            width="227"
            height="245"
            alt={NO_PUBLIC_EBOOKS}
          />
        </div>
      );
    } else if (menu === 'trash') {
      empty = (
        <div className={cx('trash_empty')}>
          <img
            src={emptyTrash}
            width="186"
            height="186"
            alt={EMPTY_TRASH_MESSAGE}
          />
          <span>{EMPTY_TRASH_MESSAGE}</span>
        </div>
      );
    }
    return empty;
  }

  return (
    <div
      className={cx('library_list', `library--${layoutType}`, {
        'details--opened': isDetailsOpened,
      })}
    >
      {layoutType === 'list' && <Header />}
      <SimpleBar forceVisible={true} ref={simplebarRef}>
        <ul className={cx('book_list', menu)} ref={listRef}>
          {status === 'pending' && (
            <li>
              <UploadIndicator />
            </li>
          )}
          {list.map((item: any) => {
            const itemId =
              item?.project?.projectId ??
              item?.resource?.resourceId ??
              item.resource.seq;
            return (
              <li
                key={itemId}
                ref={intersectRef as RefObject<HTMLLIElement>}
                className={cx({
                  active: selectedIds?.includes(itemId),
                })}
              >
                <Book
                  entity={item}
                  item={item.project || item.resource}
                  classnames={[bookItemClass]}
                />
              </li>
            );
          })}
          {!isMobile &&
            !list.length &&
            menu !== 'search' &&
            menu !== 'trash' && (
              <li>
                <div className={cx('book_items', layoutType)}>
                  {menu === 'library' || menu === 'mydrive' ? (
                    <Input type="file" id={`${menu}_upload`}>
                      <label
                        htmlFor={`${menu}_upload`}
                        className={cx('insert_book')}
                      >
                        <i className={cx('svg_insert_book')}></i>
                      </label>
                    </Input>
                  ) : (
                    <div
                      className={cx('btn', 'insert_book', {
                        hidden: menu === 'mydrive' && isHover,
                      })}
                      onClick={() =>
                        menu === 'share' &&
                        toggleModal({
                          name: 'linkRegistration',
                          id: 'linkRegistration',
                          type: 'alert',
                          initialButtonDisabled: true,
                        })
                      }
                      {...hoverEventHandlers}
                    >
                      <i className={cx('svg_insert_book')}></i>
                    </div>
                  )}
                </div>
              </li>
            )}
        </ul>
        {!list.length &&
          (isMobile && menu !== 'search' && menu !== 'trash' ? (
            <div className={cx('library_empty')}>
              <img src={library_empty}></img>
              <span>{EMPTY_EBOOK}</span>
            </div>
          ) : (
            <Empty />
          ))}
      </SimpleBar>
    </div>
  );
}
